import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { inr, classNames } from '@/lib/format';
import { imgSrc } from '@/lib/img';

const BADGE_STYLES = {
  customizable: 'bg-[var(--bb-pink)] text-white',
  new: 'bg-[var(--bb-cyan)] text-white',
  bestseller: 'bg-[var(--bb-yellow)] text-[var(--bb-darkblue)]',
  limited: 'bg-[var(--bb-darkblue)] text-white',
};
const BADGE_LABEL = {
  customizable: 'Customizable',
  new: 'New',
  bestseller: 'Bestseller',
  limited: 'Limited',
};

export const ProductCard = ({ product }) => {
  const discount =
    product.compare_at_price && product.compare_at_price > product.price
      ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)
      : 0;

  return (
    <Link
      to={`/product/${product.slug}`}
      className="group block rounded-2xl bg-white border-2 border-[var(--bb-darkblue)]/10 overflow-hidden hover:-translate-y-1 hover:border-[var(--bb-cyan)]/40 hover:bb-shadow-lift transition-all"
      data-testid={`product-card-${product.slug}`}
    >
      <div className="relative aspect-square w-full overflow-hidden bg-[var(--bb-yellow-soft)]/30">
        <img
          src={imgSrc(product.image, 'card')}
          alt={product.name}
          loading="lazy"
          decoding="async"
          className={classNames(
            'absolute inset-0 w-full h-full object-contain p-2 group-hover:scale-[1.04] transition-transform duration-300',
            product.coming_soon && 'opacity-70 saturate-[0.85]'
          )}
        />
        <div className="absolute top-2 left-2 flex flex-col gap-1.5">
          {product.coming_soon && (
            <Badge
              className="rounded-full px-2.5 py-1 text-[10px] font-nunito-black bg-[var(--bb-darkblue)] text-white uppercase tracking-wider"
              data-testid={`product-coming-soon-${product.slug}`}
            >
              Coming Soon
            </Badge>
          )}
          {!product.coming_soon && (product.badges || []).slice(0, 2).map((b) => (
            <Badge
              key={b}
              className={classNames(
                'rounded-full px-2.5 py-1 text-[10px] font-nunito-black',
                BADGE_STYLES[b] || 'bg-[var(--bb-darkblue)] text-white',
              )}
            >
              {BADGE_LABEL[b] || b}
            </Badge>
          ))}
        </div>
        {!product.coming_soon && discount > 0 && (
          <div className="absolute top-2 right-2 rounded-full bg-[var(--bb-yellow)] px-2 py-0.5 text-[11px] font-nunito-black text-[var(--bb-darkblue)]">
            -{discount}%
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="text-[11px] text-[var(--bb-cyan)] uppercase tracking-wide font-nunito-black">
          {product.category_label}
        </div>
        <h3 className="mt-0.5 text-sm font-nunito-black text-[var(--bb-darkblue)] line-clamp-2 leading-snug">
          {product.name}
        </h3>
        {product.pack_label && (
          <div
            className="mt-0.5 text-[11px] font-nunito-bold text-[var(--bb-darkgrey)]"
            data-testid={`product-pack-label-${product.slug}`}
          >
            {product.pack_label}
          </div>
        )}
        <div className="mt-2 flex items-center gap-1.5 text-xs text-[var(--bb-darkgrey)] font-nunito-medium">
          <Star className="w-3.5 h-3.5 fill-[var(--bb-yellow)] text-[var(--bb-yellow)]" />
          <span>{product.rating?.toFixed?.(1) || '4.8'}</span>
          <span>·</span>
          <span>{product.reviews} reviews</span>
        </div>
        <div className="mt-2 flex items-baseline justify-between gap-2">
          <div className="flex items-baseline gap-1.5">
            {product.coming_soon ? (
              <span className="text-xs font-nunito-black text-[var(--bb-darkblue)] uppercase tracking-wider" data-testid={`product-price-${product.slug}`}>
                Notify me
              </span>
            ) : (
              <>
                <span className="text-base font-display text-[var(--bb-darkblue)]" data-testid={`product-price-${product.slug}`}>{inr(product.price)}</span>
                {discount > 0 && (
                  <span className="text-xs text-[var(--bb-lightgrey)] line-through font-nunito-medium">{inr(product.compare_at_price)}</span>
                )}
              </>
            )}
          </div>
          {!product.coming_soon && product.customizable && (
            <span className="inline-flex items-center gap-1 text-[11px] font-nunito-black text-[var(--bb-pink)]">
              <Sparkles className="w-3.5 h-3.5" /> Custom
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};
